import React, { useState } from 'react';
import { X } from 'lucide-react';
import { ProspectInput } from '../../types';

interface ProspectFormModalProps {
    onClose: () => void;
    onSave: (prospect: ProspectInput) => Promise<void> | void;
    isSaving?: boolean;
    /** Datos iniciales al editar; vacío al dar de alta. */
    initial?: Partial<ProspectInput>;
}

// D2 — alta y edición de una persona de prueba, en su propio modal.
//
// Mismo patrón que el resto de altas: el listado abre un formulario aparte, no
// lo despliega dentro de la propia lista.
//
// No se pide el DNI: es justo lo que no se le pide a quien viene a probar. Hace
// falta al convertirla en cliente, y entonces lo pide el formulario de
// inscripción.
const ProspectFormModal: React.FC<ProspectFormModalProps> = ({ onClose, onSave, isSaving = false, initial }) => {
    const [form, setForm] = useState<ProspectInput>({
        name: initial?.name ?? '',
        phone: initial?.phone ?? '',
        email: initial?.email ?? '',
        notes: initial?.notes ?? '',
    });
    const [touched, setTouched] = useState(false);

    const faltan = !form.name.trim() || !form.phone.trim() || !form.email.trim();

    const handleSave = async () => {
        setTouched(true);
        if (faltan) return;
        await onSave({ ...form, name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim() });
    };

    const campo = (
        label: string,
        key: keyof ProspectInput,
        opciones: { type?: string; required?: boolean; placeholder?: string } = {},
    ) => (
        <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">
                {label} {opciones.required && <span className="text-red-500">*</span>}
            </label>
            <input
                type={opciones.type ?? 'text'}
                value={form[key] ?? ''}
                onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
                placeholder={opciones.placeholder}
                className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    touched && opciones.required && !String(form[key] ?? '').trim()
                        ? 'border-red-400'
                        : 'border-gray-300'
                }`}
            />
        </div>
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center overflow-y-auto h-full w-full p-4 z-50">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-lg mx-auto">
                <div className="flex justify-between items-center p-6 border-b">
                    <h2 className="text-xl font-bold text-gray-800">Nueva persona de prueba</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6">
                    <p className="text-xs text-gray-500 mb-4">
                        Alguien que viene a <strong>probar</strong> una clase. No ocupa plaza fija, pero sí
                        sitio en la clase a la que se le apunte.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {campo('Nombre', 'name', { required: true })}
                        {campo('Teléfono', 'phone', { required: true })}
                    </div>

                    <div className="mt-4">
                        {campo('Correo', 'email', { required: true, type: 'email' })}
                    </div>

                    <div className="mt-4">
                        {campo('Notas', 'notes', { placeholder: 'Opcional — por ejemplo, quién la trajo' })}
                    </div>

                    <p className="text-[11px] text-gray-400 mt-3">
                        No se pide el DNI: hace falta solo si luego se da de alta como cliente.
                    </p>

                    {touched && faltan && (
                        <p className="text-xs text-red-500 mt-2">
                            Nombre, teléfono y correo son obligatorios.
                        </p>
                    )}
                </div>

                <div className="flex justify-end gap-3 p-6 border-t">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className={`px-4 py-2 text-white rounded ${isSaving ? 'bg-gray-400' : 'bg-brand-primary hover:brightness-90'}`}
                    >
                        Guardar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProspectFormModal;
